import React, {useMemo} from 'react';
import {Card, ProgressBar} from 'react-bootstrap';
import {ColumnDef} from '@tanstack/react-table';
import TableMetronic from './../../../../app/common/components/table/TableMetronic';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import {EmailAccount} from '../../Pages/Email-accounts/DomainEmailAccounts';
import {getProgressVariant} from '../../Helpers/EmailAccounts/quotaUtils';

interface EmailAccountsTableCardProps {
  emailAccounts: EmailAccount[];
  onAddAccount: () => void;
  onEditAccount: (account: EmailAccount) => void;
  onDeleteAccount: (account: EmailAccount) => void;
  onAddAlias: (account: EmailAccount) => void;
  onDeleteAlias: (account: EmailAccount, alias: string) => void;
  onManageLists: () => void;
  onManageOutOfOffice: (account: EmailAccount) => void;
  isLoading?: boolean;
  domainType?: string;
}

const EmailAccountsTableCard: React.FC<EmailAccountsTableCardProps> = ({
  emailAccounts,
  onAddAccount,
  onEditAccount,
  onDeleteAccount,
  onAddAlias,
  onDeleteAlias,
  onManageLists,
  onManageOutOfOffice,
  isLoading = false,
  domainType = '',
}) => {
  const renderQuotaCell = (account: EmailAccount): JSX.Element => {
    // Quota illimité (quota = 0)
    if (account.quota === 0) {
      return (
        <div className="d-flex justify-content-between">
          <small className="text-muted">
            {account.quotaUsage ? `${(account.quotaUsage / 1000000).toFixed(0)} Mo` : '0 Mo'} /
            illimité
          </small>
        </div>
      );
    }

    // Quota limité
    const quotaUsageMo = account.quotaUsage ? account.quotaUsage / 1000000 / 1000000 : 0;
    const quotaMo = account.quota / 1000000 / 1000000;
    const percentage = account.quota > 0 ? Math.round((quotaUsageMo / quotaMo) * 100) : 0;
    const variant = getProgressVariant(percentage);

    return (
      <div>
        <div className="d-flex justify-content-between mb-1">
          <small className="text-muted">
            {quotaUsageMo.toFixed(0)} Mo / {quotaMo.toFixed(0)} Mo
          </small>
          <small className="fw-bold">{percentage}%</small>
        </div>

        <div
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            overflow: 'hidden',
            backgroundColor: '#f8f9fc',
          }}
        >
          <ProgressBar
            now={percentage}
            variant={variant}
            style={{height: '10px', borderRadius: '0'}}
          />
        </div>
      </div>
    );
  };

  const renderAliasCell = (account: EmailAccount): JSX.Element => {
    const aliasList = account.aliases;

    return (
      <div style={{overflow: 'hidden'}}>
        <div className="pe-3">
          {aliasList.length > 0 && (
            <div className="mb-2">
              {aliasList.map((aliasObj, index) => (
                <div
                  key={index}
                  className="d-flex align-items-center justify-content-between mb-1 p-1 bg-light rounded"
                >
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{minWidth: 0, flex: '1 1 auto'}}
                  >
                    <small
                      className="text-muted text-truncate"
                      style={{minWidth: 0}}
                      title={aliasObj.alias}
                    >
                      {aliasObj.alias}
                    </small>
                    {!aliasObj.active && (
                      <span className="badge badge-sm bg-secondary flex-shrink-0">Inactif</span>
                    )}
                  </div>
                  <button
                    className="btn btn-sm btn-icon btn-light-danger flex-shrink-0 ms-2"
                    style={{padding: '2px 6px'}}
                    onClick={() => onDeleteAlias(account, aliasObj.alias)}
                    title="Supprimer cet alias"
                  >
                    <i className="fa-solid fa-times fs-8"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn btn-sm btn-light-primary"
              style={{padding: '4px 10px', fontSize: '11px'}}
              onClick={() => onAddAlias(account)}
            >
              <i className="fa-solid fa-plus me-1" style={{fontSize: '10px'}}></i>
              Ajouter un alias
            </button>
            {domainType !== 'zimbra' && (
              <button
                className="btn btn-sm btn-light-info"
                style={{padding: '4px 10px', fontSize: '11px'}}
                onClick={() => onManageOutOfOffice(account)}
                title="Gérer le message d'absence"
              >
                <i className="fa-solid fa-calendar-xmark me-1" style={{fontSize: '10px'}}></i>
                Message d'absence
                {account.vacationMessage?.active && (
                  <i
                    className="fa-solid fa-circle-check ms-1 text-success"
                    style={{fontSize: '10px'}}
                  ></i>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderActionsCell = (account: EmailAccount): JSX.Element => (
    <div className="d-flex gap-2">
      <button
        className="btn btn-sm btn-icon btn-light-primary"
        onClick={() => onEditAccount(account)}
        title="Modifier"
      >
        <i className="fa-solid fa-pen fs-6"></i>
      </button>
      <button
        className="btn btn-sm btn-icon btn-light-danger"
        onClick={() => onDeleteAccount(account)}
        title="Supprimer"
      >
        <i className="fa-solid fa-trash fs-6"></i>
      </button>
    </div>
  );

  const columns = useMemo<ColumnDef<EmailAccount>[]>(
    () => [
      {
        header: 'Compte',
        accessorKey: 'username',
        size: 200,
        cell: (info) => <span className="fw-bold">{info.getValue() as string}</span>,
      },
      {
        header: 'Nom',
        accessorKey: 'name',
        size: 150,
      },
      {
        header: 'État',
        accessorKey: 'active',
        size: 90,
        cell: (info) => {
          const isActive = info.getValue() as boolean;
          const badgeClass = isActive ? 'badge-success' : 'badge-danger';
          return <span className={`badge ${badgeClass}`}>{isActive ? 'Actif' : 'Inactif'}</span>;
        },
      },
      {
        header: 'Occupation / Quota',
        accessorKey: 'quotaUsage',
        size: 200,
        cell: (info) => renderQuotaCell(info.row.original),
      },
      {
        header: 'Alias',
        accessorKey: 'aliases',
        size: 450,
        cell: (info) => renderAliasCell(info.row.original),
      },
      {
        header: 'Actions',
        size: 110,
        cell: (info) => renderActionsCell(info.row.original),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditAccount, onDeleteAccount, onAddAlias, onDeleteAlias, onManageOutOfOffice, domainType]
  );

  const renderMobileCard = (account: EmailAccount): JSX.Element => (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <span className="fw-bold" style={{minWidth: 0, wordBreak: 'break-word'}}>
          {account.username}
        </span>
        <span
          className={`badge flex-shrink-0 ${account.active ? 'badge-success' : 'badge-danger'}`}
        >
          {account.active ? 'Actif' : 'Inactif'}
        </span>
      </div>
      {account.name && <div className="text-muted">{account.name}</div>}
      <div>
        <span className="text-muted fs-8 fw-semibold text-uppercase d-block mb-1">
          Occupation / Quota
        </span>
        {renderQuotaCell(account)}
      </div>
      <div>
        <span className="text-muted fs-8 fw-semibold text-uppercase d-block mb-1">Alias</span>
        {renderAliasCell(account)}
      </div>
      <div className="pt-1">{renderActionsCell(account)}</div>
    </div>
  );

  return (
    <Card>
      <Card.Header>
        <Card.Title className="d-flex flex-wrap gap-3 align-items-center justify-content-between w-100">
          <Level3Title title="Comptes de messagerie existants" />
          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-sm btn-primary" onClick={onAddAccount}>
              <i className="fa-solid fa-plus me-2"></i>
              Ajouter un compte
            </button>
            {/* <button className="btn btn-sm btn-secondary">
              <i className="fa-solid fa-file-import me-2"></i>
              Ajout de comptes en masse
            </button> */}
            <button className="btn btn-sm btn-info" onClick={onManageLists}>
              <i className="fa-solid fa-list-ul me-2"></i>
              Gestion des listes
            </button>
          </div>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        <TableMetronic<EmailAccount>
          data={emailAccounts}
          columns={columns}
          renderMobileCard={renderMobileCard}
          isLoading={isLoading}
          showCardHeader={false}
          searchBar={false}
          pagination
          defaultSorting={[
            {
              id: 'username',
              desc: false,
            },
          ]}
        />
      </Card.Body>
    </Card>
  );
};

export default EmailAccountsTableCard;
